import { inject, injectable } from 'inversify';

import { User } from '@project44/ui-utils';
import {
  DtoBase,
  SharedModels,
  TenantModels,
  UserModels,
} from '@project44-models/iam-gateway';

import { TenantSpecificTenantAdminRolesEnum } from '../../../UnifiedRoles/domain/enums';
import { TUnifiedRoleCollectionDto } from '../../../UnifiedRoles/domain/interfaces';
import { UserService } from '../../application/User.service';

@injectable()
export class UserServiceController {
  constructor(@inject(UserService) private readonly userService: UserService) {}

  public queryUserTenantAgnosticRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
    },
  ): Promise<{ userTenantAgnosticUnifiedRoles: TUnifiedRoleCollectionDto }> {
    return this.userService.queryUserTenantAgnosticRoles(
      userPrincipal,
      payload,
    );
  }

  public updateUserTenantAgnosticRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
      roles: DtoBase<SharedModels.UnifiedRoleCollectionViewModel>;
    },
  ): Promise<void> {
    const { user, tenant, roles } = payload;

    return this.userService.updateUserTenantAgnosticRoles(userPrincipal, {
      user,
      tenant,
      roles,
    });
  }

  public queryUserTenantSpecificRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
    },
  ): Promise<{ userTenantSpecificUnifiedRoles: TUnifiedRoleCollectionDto }> {
    return this.userService.queryUserTenantSpecificRoles(
      userPrincipal,
      payload,
    );
  }

  public updateUserTenantSpecificRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
      roles: DtoBase<SharedModels.UnifiedRoleCollectionViewModel>;
    },
  ): Promise<void> {
    const { user, tenant, roles } = payload;
    return this.userService.updateUserTenantSpecificRoles(userPrincipal, {
      user,
      tenant,
      roles,
    });
  }

  public changeUserTenantSpecificAdminRolesType(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
      tenantSpecificTenantAdminPolicy: TenantSpecificTenantAdminRolesEnum;
      featureFlags: Record<string | symbol | number, boolean>;
    },
  ): Promise<void> {
    return this.userService.changeUserTenantSpecificAdminRolesType(
      userPrincipal,
      payload,
    );
  }

  public createUser(
    ...createUserArgs: Parameters<UserService['createUser']>
  ): Promise<{ user: DtoBase<UserModels.UserViewModel> }> {
    return this.userService.createUser(...createUserArgs);
  }

  public readonly getUserById = (
    ...getUserByIdArgs: Parameters<UserService['getUserById']>
  ): Promise<{ user: DtoBase<UserModels.UserViewModel> }> => {
    return this.userService.getUserById(...getUserByIdArgs);
  };
}
